// src/store/notice/noticeStore.ts
import { create } from "zustand";
import type {
  NoticeItem,
  NoticeListQuery,
  PageContent,
  NoticeUpsertPayload,
} from "src/types/notice/noticeTypes";
import {
  getNoticeListApi,
  createNoticeApi,
  updateNoticeApi,
  deleteNoticeApi,
  setNoticePinApi,
  setNoticeActiveApi,
} from "src/api/notice/noticeApi";

type NoticeState = {
  query: NoticeListQuery;
  pageData: PageContent<NoticeItem> | null;

  loading: boolean;
  error: string | null;

  // actions
  setQuery: (patch: Partial<NoticeListQuery>) => void;
  resetQuery: () => void;
  fetchList: () => Promise<void>;

  createNotice: (args: { payload: NoticeUpsertPayload; file?: File | null }) => Promise<boolean>;
  updateNotice: (args: {
    noticeId: number;
    payload: NoticeUpsertPayload;
    file?: File | null;
  }) => Promise<boolean>;
  deleteNotice: (noticeId: number) => Promise<boolean>;

  setPinned: (args: { noticeId: number; pinnedYn: boolean }) => Promise<boolean>;
  setActive: (args: { noticeId: number; activeYn: boolean }) => Promise<boolean>;
};

const defaultQuery: NoticeListQuery = {
  noticeTarget: undefined,
  noticeTtl: "",
  createdId: "",
  activeYn: undefined,
  activeStrDatetime: "",
  activeEndDatetime: "",
  page: 0,
  size: 10,
  order: "pinnedYn,desc:noticeOrder,asc:createdAt,desc",
};

const isOk = (statusCode?: number) =>
  statusCode === 1 || statusCode === 0 || statusCode === undefined;

export const useNoticeStore = create<NoticeState>((set, get) => ({
  query: defaultQuery,
  pageData: null,

  loading: false,
  error: null,

  setQuery: (patch) => set((s) => ({ query: { ...s.query, ...patch } })),
  resetQuery: () => set({ query: defaultQuery }),

  fetchList: async () => {
    const { query } = get();
    set({ loading: true, error: null });
    try {
      const res = await getNoticeListApi(query);
      if (!isOk(res.statusCode)) throw new Error(res.message || "공지사항 목록 조회 실패");
      set({ pageData: res.data, loading: false });
    } catch (e: any) {
      set({ loading: false, error: e?.message || "공지사항 목록 조회 중 오류" });
    }
  },

  createNotice: async ({ payload, file }) => {
    set({ loading: true, error: null });
    try {
      const res = await createNoticeApi(payload, file);
      if (!isOk(res.statusCode)) throw new Error(res.message || "공지사항 생성 실패");
      set({ loading: false });
      await get().fetchList();
      return true;
    } catch (e: any) {
      set({ loading: false, error: e?.message || "공지사항 생성 중 오류" });
      return false;
    }
  },

  updateNotice: async ({ noticeId, payload, file }) => {
    set({ loading: true, error: null });
    try {
      const res = await updateNoticeApi(noticeId, payload, file);
      if (!isOk(res.statusCode)) throw new Error(res.message || "공지사항 수정 실패");
      set({ loading: false });
      await get().fetchList();
      return true;
    } catch (e: any) {
      set({ loading: false, error: e?.message || "공지사항 수정 중 오류" });
      return false;
    }
  },

  deleteNotice: async (noticeId) => {
    set({ loading: true, error: null });
    try {
      const res = await deleteNoticeApi(noticeId);
      if (!isOk(res.statusCode)) throw new Error(res.message || "공지사항 삭제 실패");
      set({ loading: false });
      await get().fetchList();
      return true;
    } catch (e: any) {
      set({ loading: false, error: e?.message || "공지사항 삭제 중 오류" });
      return false;
    }
  },

  setPinned: async ({ noticeId, pinnedYn }) => {
    set({ loading: true, error: null });
    try {
      const res = await setNoticePinApi(noticeId, pinnedYn);
      if (!isOk(res.statusCode)) throw new Error(res.message || "상단 고정 설정 실패");
      set({ loading: false });
      await get().fetchList();
      return true;
    } catch (e: any) {
      set({ loading: false, error: e?.message || "상단 고정 처리 중 오류" });
      return false;
    }
  },

  setActive: async ({ noticeId, activeYn }) => {
    set({ loading: true, error: null });
    try {
      const res = await setNoticeActiveApi(noticeId, activeYn);
      if (!isOk(res.statusCode)) throw new Error(res.message || "활성/비활성 설정 실패");
      set({ loading: false });
      await get().fetchList();
      return true;
    } catch (e: any) {
      set({ loading: false, error: e?.message || "활성/비활성 처리 중 오류" });
      return false;
    }
  },
}));
